import { useState, useEffect, useCallback } from 'react';
import notificationService from '../services/notificationService';
import socketService from '../services/socketService';
import useAuthStore from '../store/authStore';

/**
 * Custom hook for managing notifications with real-time updates
 * @param {Object} options - Hook options
 * @param {boolean} options.enableRealTime - Enable real-time updates via WebSocket
 * @param {boolean} options.enableBrowserNotifications - Enable browser notifications
 * @returns {Object} Notification state and methods
 */
export function useNotifications({
  enableRealTime = true,
  enableBrowserNotifications = true
} = {}) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);

  const { isAuthenticated } = useAuthStore();

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (options = {}) => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationService.getNotifications(options);
      
      if (response.success) {
        setNotifications(response.data.notifications);
        return response.data;
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.count);
        return response.data.count;
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
      throw err;
    }
  }, [isAuthenticated]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date() }
            : notification
        )
      );

      // Decrease unread count
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Mark as read via WebSocket
      if (socketService.isConnected()) {
        socketService.markNotificationAsRead(notificationId);
      }

      return true;
    } catch (err) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, []);

  // Mark notification as unread
  const markAsUnread = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsUnread(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, isRead: false, readAt: null }
            : notification
        )
      );

      // Increase unread count
      setUnreadCount(prev => prev + 1);

      return true;
    } catch (err) {
      console.error('Error marking notification as unread:', err);
      throw err;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ 
          ...notification, 
          isRead: true, 
          readAt: new Date() 
        }))
      );

      // Reset unread count
      setUnreadCount(0);

      return true;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Remove from local state
      const deletedNotification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => 
        prev.filter(notification => notification._id !== notificationId)
      );

      // Decrease unread count if the deleted notification was unread
      if (deletedNotification && !deletedNotification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return true;
    } catch (err) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  }, [notifications]);

  // Handle new notification from WebSocket
  const handleNewNotification = useCallback((notification) => {
    // Add to notifications list
    setNotifications(prev => [notification, ...prev]);

    // Increment unread count
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  // Handle notification read confirmation from WebSocket
  const handleNotificationReadConfirmed = useCallback((data) => {
    // Update local state
    setNotifications(prev => 
      prev.map(notification => 
        notification._id === data.notificationId 
          ? { ...notification, isRead: true, readAt: new Date() }
          : notification
      )
    );
  }, []);

  // Setup WebSocket connection and event listeners
  useEffect(() => {
    if (!isAuthenticated || !enableRealTime) {
      return;
    }

    // Connect to WebSocket (no token needed since we use cookies)
    socketService.connect();
    setConnected(socketService.isConnected());

    // Setup event listeners
    socketService.on('notification', handleNewNotification);
    socketService.on('notification_read_confirmed', handleNotificationReadConfirmed);
    socketService.on('connect', () => {
      setConnected(true);
    });
    socketService.on('disconnect', () => {
      setConnected(false);
    });

    // Request browser notification permission
    if (enableBrowserNotifications) {
      socketService.requestNotificationPermission();
    }

    // Cleanup on unmount
    return () => {
      socketService.off('notification', handleNewNotification);
      socketService.off('notification_read_confirmed', handleNotificationReadConfirmed);
    };
  }, [
    isAuthenticated, 
    enableRealTime, 
    enableBrowserNotifications, 
    handleNewNotification, 
    handleNotificationReadConfirmed
  ]);

  // Initial data fetch
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [isAuthenticated, fetchNotifications, fetchUnreadCount]);

  // Cleanup WebSocket on logout
  useEffect(() => {
    if (!isAuthenticated) {
      socketService.disconnect();
      setConnected(false);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated]);

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,
    connected,
    
    // Methods
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    
    // Utility methods
    refresh: () => {
      fetchNotifications();
      fetchUnreadCount();
    },
    
    // WebSocket methods
    connectWebSocket: () => {
      if (isAuthenticated) {
        socketService.connect();
      }
    },
    
    disconnectWebSocket: () => {
      socketService.disconnect();
      setConnected(false);
    },
    
    getConnectionStatus: () => socketService.getStatus()
  };
}

export default useNotifications;
